/* eslint-disable react/jsx-pascal-case */
/* ===========================================================
   키즈다 뉴스 — 본문 컴포넌트 (네비/카드/섹션/상세/코너/검색)
   원본 오프라인 프로토타입 본문을 그대로 이식 (header/footer 제외)
   =========================================================== */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { CORNERS, cornerOf, topicOf, descByName, normalizeCat } from "./data";
import { getNewsAllPaged, resolveNewsDate, getNewsByCategory, getNewsByCategoryPaged, getNewsSearch } from "./news_api";
import QuillContent from "./quill_content";
import {
  Ico,
  Thumb,
  CatTag,
  Hashtags,
  RichBody,
  fmtViews,
  relDate,
} from "./ui";

/* 키즈다 뉴스 내 모든 "앱 다운로드" CTA 이동 경로 */
const APP_DOWNLOAD_PATH = "/intro";

/* 세션 메모리 캐시 — 상세 페이지 진입 후 뒤로가기 시 재호출/초기화 방지.
   key별로 마지막 상태(목록/페이지/확정날짜)를 보관했다가 재마운트 시 복원한다. */
const _kdCache = new Map();
// 진행 중인 동일 요청을 공유해 중복 호출 방지 (StrictMode 이중 실행/재렌더 대비)
const _kdInflight = new Map();

/* 캐시에 보관된 목록에서 특정 기사(postSeq)의 조회수를 실제 값(views)으로 설정.
   상세에서 받아온 서버 조회수(webReadCount)를 그대로 반영 →
   목록으로(뒤로가기·"목록으로"·메뉴) 돌아가도 재조회 없이 상세와 동일한 값이 보인다.
   (동시 조회로 서버가 +2여도 상세=목록 일치 / 스크롤 복원 유지)
   배열/객체 참조를 새로 만들어 복원 시 리렌더가 일어나게 한다.
   캐시 값 형태: cat → 배열, corner/today/search → { ..., data: { items } } */
export function setViewCountInCache(postSeq, views) {
  if (views == null) return;
  const id = String(postSeq);
  const setItem = (a) =>
    a && String(a.id) === id ? { ...a, views } : a;
  const setArr = (arr) => (Array.isArray(arr) ? arr.map(setItem) : arr);

  for (const [key, val] of _kdCache.entries()) {
    if (Array.isArray(val)) {
      _kdCache.set(key, setArr(val));
    } else if (val && val.data && Array.isArray(val.data.items)) {
      _kdCache.set(key, { ...val, data: { ...val.data, items: setArr(val.data.items) } });
    }
  }
}

/* ---- 카테고리 정보 헬퍼 ----
   API 기사(a.apiCategoryName/apiCategoryColor)면 그 값을, 정적 시드 기사면
   corner 키로 cornerOf() 결과를 사용한다. {name, color} 반환 */
function catInfo(a) {
  if (a && a.apiCategoryName) {
    return { name: a.apiCategoryName, color: a.apiCategoryColor || "var(--blue-600)" };
  }
  return cornerOf(a ? a.corner : undefined);
}

/* ---- "키즈다 뉴스" 섹션 좌측 배너 카드 (API 배너 노출용) ----
   배너 데이터(banner)가 있을 때만 사용. 이미지 + 그라데이션 + 제목/설명 오버레이.
   클릭 시 contentsType에 따라 외부 URL / 기사 상세 / 앱 다운로드로 이동 */
function NewsBannerCard({ banner, onOpen }) {
  const navigate = useNavigate();
  const handleClick = () => {
    const ref = banner.contentsRef;
    // 앱 banner_slide_widget 로직 이식:
    //   NEWS → 기사 상세(contentsRef = postSeq), URL → 외부 링크
    if (banner.contentsType === "NEWS" && ref) {
      onOpen({ id: String(ref) });
    } else if (banner.contentsType === "URL" && ref) {
      window.open(ref, "_blank", "noopener,noreferrer");
    } else if (ref) {
      window.open(ref, "_blank", "noopener,noreferrer");
    } else {
      navigate(APP_DOWNLOAD_PATH);
    }
  };
  return (
    <article onClick={handleClick} style={{ cursor: "pointer", position: "relative", height: "100%", minHeight: 420, borderRadius: "var(--r-lg)", overflow: "hidden", boxShadow: "var(--sh-2)" }} className="kd-card">
      {banner.image
        ? <img src={banner.image} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        : <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, hsl(215 70% 58%), hsl(243 75% 44%))" }} />}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(8,16,40,.82) 0%, rgba(8,16,40,.2) 50%, transparent 78%)" }} />
      <span style={{ position: "absolute", left: 18, top: 18, zIndex: 2, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(15, 23, 42, 0.78)", color: "#fff", fontSize: 12, fontWeight: 800, padding: "6px 12px", borderRadius: 99, backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)" }}>
        <span style={{ width: 6, height: 6, borderRadius: 99, background: "rgb(255, 90, 77)" }} />
        {" AD · 광고"}
      </span>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "26px 30px 22px", color: "#fff" }}>
        {banner.title && <h2 className="clamp-2" style={{ margin: 0, fontSize: 28, fontWeight: 900, lineHeight: 1.25, letterSpacing: "-.02em", maxWidth: 600 }}>{banner.title}</h2>}
        {banner.description && <p className="clamp-2" style={{ margin: "10px 0 0", fontSize: 15, color: "rgba(255,255,255,.86)", lineHeight: 1.55, maxWidth: 560 }}>{banner.description}</p>}
      </div>
    </article>
  );
}

/* ---------- Section header ---------- */
export function SectionHead({ color, eyebrow, title, desc, more }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 20, gap: 16 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 20, borderRadius: 3, background: color || "var(--blue-600)" }} />
          <span className="kicker" style={{ color: color || "var(--blue-600)" }}>{eyebrow}</span>
        </div>
        <h2 style={{ margin: "8px 0 0", fontSize: 24, fontWeight: 900, letterSpacing: "-.02em" }}>{title}</h2>
        {desc && <p style={{ margin: "5px 0 0", fontSize: 14, color: "var(--muted)" }}>{desc}</p>}
      </div>
      {more && <button onClick={more} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13.5, fontWeight: 700, color: "var(--ink-2)" }}>더보기 <Ico.chevR width={15} height={15} /></button>}
    </div>
  );
}

/* ---------- Sticky category nav (전체 드롭다운 · 카테고리 · 우측 검색) ---------- */
export function CornerNav({ corners, active, onPick, onHome, query, onSearch }) {
  const navigate = useNavigate();
  const [v, setV] = useState(query || "");
  const [openS, setOpenS] = useState(false);
  const [openAll, setOpenAll] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => setV(query || ""), [query]);
  useEffect(() => { if (openS && inputRef.current) inputRef.current.focus(); }, [openS]);
  const pickCat = (k) => { setOpenAll(false); onPick(k); };
  return (
    <nav className="kd-cornernav" style={{ position: "sticky", top: 0, zIndex: 40, background: "var(--blue-600)", color: "#fff", boxShadow: "0 2px 14px rgba(15,42,122,.18)" }}>
      <div className="wrap" style={{ display: "flex", alignItems: "center", height: 52, gap: 2 }}>
        <button onClick={() => setOpenAll((o) => !o)} style={{ color: "#fff", display: "flex", alignItems: "center", gap: 6, fontWeight: 800, fontSize: 14, padding: "0 12px 0 0", marginRight: 4, borderRight: "1px solid rgba(255,255,255,.22)", height: 24, position: "relative", flexShrink: 0 }}>
          <Ico.grid width={16} height={16} /> 메뉴
          <Ico.chevD width={13} height={13} style={{ transform: openAll ? "rotate(180deg)" : "none", transition: "transform .18s" }} />
        </button>
        <div className="kd-noscroll" style={{ display: "flex", alignItems: "center", gap: 0, overflowX: "auto", flex: 1, scrollbarWidth: "none" }}>
          <button onClick={onHome} style={{ color: "#fff", fontWeight: active ? 600 : 800, fontSize: 14, padding: "0 11px", height: 52, whiteSpace: "nowrap", position: "relative", opacity: active ? .84 : 1 }}>
            전체
            {!active && <span style={{ position: "absolute", left: 11, right: 11, bottom: 0, height: 3, background: "#fff", borderRadius: 3 }} />}
          </button>
          {corners.map((c) => {
            const on = active === c.key;
            return (
              <button key={c.key} onClick={() => onPick(c.key)} style={{ color: "#fff", fontWeight: on ? 800 : 600, fontSize: 14, padding: "0 11px", height: 52, whiteSpace: "nowrap", position: "relative", opacity: on ? 1 : .84 }}>
                {c.name}
                {on && <span style={{ position: "absolute", left: 11, right: 11, bottom: 0, height: 3, background: "#fff", borderRadius: 3 }} />}
              </button>
            );
          })}
        </div>
        {/* 우측 검색 (아이콘 → 클릭 시 펼침) */}
        <div style={{ flexShrink: 0, marginLeft: 8, display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          {openS ? (
            <form onSubmit={(e) => { e.preventDefault(); onSearch(v); }} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.16)", borderRadius: 99, height: 36, padding: "0 4px 0 14px", width: 228, animation: "kd-fade .15s" }}>
              <input ref={inputRef} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { if (!v.trim()) setOpenS(false); }} placeholder="궁금한 뉴스를 검색해 보세요" style={{ border: "none", background: "none", outline: "none", flex: 1, fontSize: 13.5, color: "#fff", minWidth: 0 }} className="kd-navsearch" />
              <button type="submit" aria-label="검색" style={{ width: 28, height: 28, borderRadius: 99, background: "#fff", color: "var(--blue-600)", display: "grid", placeItems: "center", flexShrink: 0 }}><Ico.search width={15} height={15} /></button>
            </form>
          ) : (
            <button onClick={() => setOpenS(true)} aria-label="검색 열기" style={{ width: 38, height: 38, borderRadius: 99, background: "rgba(255,255,255,.16)", color: "#fff", display: "grid", placeItems: "center" }} className="kd-navsearchbtn">
              <Ico.search width={18} height={18} />
            </button>
          )}
        </div>
      </div>

      {/* 전체 드롭다운 (메가 메뉴) */}
      {openAll && (
        <>
          <div onClick={() => setOpenAll(false)} style={{ position: "fixed", inset: 0, top: 0, zIndex: -1 }} />
          <div style={{ position: "absolute", top: 52, left: 0, right: 0, background: "#fff", color: "var(--ink)", boxShadow: "var(--sh-3)", borderBottom: "1px solid var(--line)" }}>
            <div className="wrap" style={{ padding: "24px 24px 28px", display: "grid", gridTemplateColumns: "1.9fr 1fr", gap: 36 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 13 }}>
                  <div className="kicker" style={{ color: "var(--muted)" }}>카테고리</div>
                  <button onClick={() => { setOpenAll(false); onHome(); }} style={{ fontSize: 13, fontWeight: 800, color: "var(--blue-600)", display: "inline-flex", alignItems: "center", gap: 5 }}><Ico.grid width={15} height={15} /> 전체 뉴스 홈</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {corners.map((c) => {
                    const on = active === c.key;
                    return (
                      <button key={c.key} onClick={() => pickCat(c.key)} className="kd-megabtn" style={{ textAlign: "left", padding: "11px 13px", borderRadius: 11, background: on ? "var(--blue-50)" : "var(--bg-soft)", border: "1.5px solid " + (on ? "var(--blue-100)" : "transparent") }}>
                        <div style={{ fontWeight: 800, fontSize: 14.5, color: c.color }}>{c.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div style={{ padding: "26px 22px", borderRadius: 14, background: "linear-gradient(120deg, var(--blue-700), var(--blue-600))", color: "#fff" }}>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>키즈다 신문 구독</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,.85)", margin: "9px 0 18px", lineHeight: 1.65 }}>매일 아침, 교과 연계 뉴스를 메일로 받아보세요.<br />초등 눈높이에 맞춘 시사·과학·경제 이야기가 가득해요.</div>
                  <button onClick={() => navigate(APP_DOWNLOAD_PATH)} style={{ background: "#fff", color: "var(--blue-700)", fontWeight: 800, fontSize: 13.5, padding: "11px 20px", borderRadius: 99, display: "inline-flex", alignItems: "center", gap: 7 }}>
                    <Ico.download width={15} height={15} />
                    앱 다운로드</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}

/* ---------- Article cards ---------- */
export function MetaRow({ a, light }) {
  const c = light ? "rgba(255,255,255,.78)" : "var(--muted)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12.5, color: c, fontWeight: 600 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Ico.clock /> {relDate(a.date)}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Ico.eye /> {fmtViews(a.views)}</span>
    </div>
  );
}

export function CardStd({ a, onOpen }) {
  const cat = catInfo(a);
  return (
    <article onClick={() => onOpen(a)} style={{ cursor: "pointer", display: "flex", flexDirection: "column", gap: 11 }} className="kd-card">
      <Thumb src={a.image} hue={a.hue} radius={12} />
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{ flexShrink: 0 }}><CatTag color={cat.color}>{cat.name}</CatTag></span>
        <span style={{ minWidth: 0, flex: 1 }}><Hashtags tags={a.tags} size={12} max={3} nowrap /></span>
      </div>
      <h3 className="clamp-2 kd-title" style={{ margin: 0, fontSize: 17, fontWeight: 800, lineHeight: 1.35, letterSpacing: "-.01em" }}>{a.title}</h3>
      <p className="clamp-2" style={{ margin: 0, fontSize: 13.5, color: "var(--muted)", lineHeight: 1.55 }}>{a.summary}</p>
      <MetaRow a={a} />
    </article>
  );
}

export function CardRow({ a, onOpen }) {
  const cat = catInfo(a);
  return (
    <article onClick={() => onOpen(a)} style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "118px 1fr", gap: 14, alignItems: "center" }} className="kd-card">
      <Thumb src={a.image} hue={a.hue} radius={10} ratio="1/1" icon={false} />
      <div style={{ minWidth: 0 }}>
        <CatTag color={cat.color} size={11}>{cat.name}</CatTag>
        <h3 className="clamp-2 kd-title" style={{ margin: "7px 0 0", fontSize: 15, fontWeight: 800, lineHeight: 1.35 }}>{a.title}</h3>
        <div style={{ marginTop: 7 }}><MetaRow a={a} /></div>
      </div>
    </article>
  );
}

/* ---------- 메타 footer (히어로 카드 하단 슬레이트 바) ---------- */
/* ---------- 히어로 오버레이 카드 (이미지 + 그라데이션 오버레이 + 캡션 + 메타 footer) ----------
   캡션 내용(카테고리/제목/요약)은 호출부마다 폰트·문구가 달라 children으로 전달 */
function HeroCard({ article, onOpen, radius = "var(--r-lg)", marginBottom = 0, overlay, captionPad, minHeight = 420, badge, children }) {
  const hue = article.hue ?? 215;
  return (
    <article onClick={() => onOpen(article)} style={{ cursor: "pointer", position: "relative", height: "100%", minHeight, borderRadius: radius, overflow: "hidden", boxShadow: "var(--sh-2)", marginBottom }} className="kd-card">
      {article.image
        ? <img src={article.image} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        : <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, hsl(${hue} 70% 58%), hsl(${(hue + 28) % 360} 75% 44%))` }} />}
      <div style={{ position: "absolute", inset: 0, background: overlay }} />
      {badge}
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: captionPad, color: "#fff" }}>
        {children}
        <div style={{ marginTop: 14 }}><MetaRow a={article} light /></div>
      </div>
    </article>
  );
}

/* ---------- 오늘 뉴스 (날짜 선택 + 메인/사이드 + 인기/추천) ---------- */
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];
export function fmtKDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${y}년 ${m}월 ${d}일 (${WEEKDAYS[wd]})`;
}
function shiftDate(s, days) {
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}
/* 오늘 날짜 (YYYY-MM-DD, 로컬 기준) */
function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
export const TODAY = todayStr();

/* ---------- 로딩 기본(스켈레톤) 화면 ---------- */
function Skel({ h, style }) {
  return <div className="kd-skel" style={{ borderRadius: 10, height: h, ...style }} />;
}
function NewsHeroSkeleton() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 24 }} className="kd-hero">
      <Skel h="auto" style={{ aspectRatio: "16/10", borderRadius: "var(--r-lg)" }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 124px", gap: 14, alignItems: "center", paddingBottom: 16, borderBottom: "1px solid var(--line)", flex: 1 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Skel h={14} style={{ width: "38%" }} />
              <Skel h={18} />
              <Skel h={12} style={{ width: "55%" }} />
            </div>
            <Skel h="auto" style={{ aspectRatio: "1/1" }} />
          </div>
        ))}
      </div>
    </div>
  );
}
function GridSkeleton({ count = 12 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 24 }} className="kd-grid4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Skel h="auto" style={{ aspectRatio: "16/10", borderRadius: 12 }} />
          <Skel h={15} />
          <Skel h={12} style={{ width: "70%" }} />
        </div>
      ))}
    </div>
  );
}
function PickSkeleton() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.45fr", gap: 24 }} className="kd-picks">
      <div style={{ background: "#fff", borderRadius: "var(--r)", padding: "20px 22px", border: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 14 }}>
        {[0, 1, 2, 3, 4].map((i) => <Skel key={i} h={16} />)}
      </div>
      <div style={{ background: "#fff", borderRadius: "var(--r)", padding: "20px 22px", border: "1px solid var(--line)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16 }} className="kd-rec4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Skel h="auto" style={{ aspectRatio: "16/11" }} />
              <Skel h={13} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function TodayNews({ newsItems, popularItems, recommendItems, banners, loading = false, date: dateProp, onDateChange, onOpen, onMore }) {
  // 날짜는 상위(index.js)에서 제어 가능 — props가 있으면 controlled, 없으면 내부 state
  const [dateState, setDateState] = useState(TODAY);
  const date = dateProp != null ? dateProp : dateState;
  const setDate = (updater) => {
    const next = typeof updater === "function" ? updater(date) : updater;
    if (onDateChange) onDateChange(next);
    else setDateState(next);
  };
  const dateInputRef = useRef(null);

  // 홈은 API 전용 — 정적 데모 데이터로 폴백하지 않는다.
  const apiMode = Array.isArray(newsItems) && newsItems.length > 0;
  // 헤드라인 배너 목록(이미지 있는 것만) — 1개면 단일 렌더, 2개 이상이면 슬라이드.
  // 배너는 고정 노출이라 날짜를 변경해도 계속 떠 있어야 하며,
  // NEWS 배너의 표시 날짜는 선택 날짜(date)를 따라가도록 동기화한다(고정 "오늘" 방지).
  const bannerList = (Array.isArray(banners) ? banners : []).filter((b) => b && b.image);
  const hasBanner = bannerList.length > 0;
  const isMultiBanner = bannerList.length > 1;
  // 첫 로딩(데이터 아직 없음): 데모 대신 기본 스켈레톤 화면을 보여준다.
  const initialLoading = loading && !apiMode;

  const basePool = apiMode ? newsItems : [];
  const headline = basePool[0];

  const isToday = date === TODAY;

  // 우측 리스트:
  //  - 배너 노출 중인 NEWS 기사들은 중복 제외 후 3개
  //  - 배너가 없으면 헤드라인 제외 후 3개
  const bannerNewsIds = new Set(
    bannerList.filter((b) => b.contentsType === "NEWS").map((b) => String(b.id))
  );
  const sideList = hasBanner
    ? basePool.filter((a) => !bannerNewsIds.has(String(a.id))).slice(0, 3)
    : basePool.filter((a) => a.id !== (headline && headline.id)).slice(0, 3);

  const popular =
    Array.isArray(popularItems) && popularItems.length ? popularItems.slice(0, 5) : [];
  const recommend =
    Array.isArray(recommendItems) && recommendItems.length ? recommendItems.slice(0, 6) : [];

  // 우측 사이드 리스트 (배너/헤드라인 공통)
  const sideListEl = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {sideList.map((a) => {
        const ci = catInfo(a);
        return (
          <article key={a.id} onClick={() => onOpen(a)} style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 124px", gap: 14, alignItems: "center", paddingBottom: 16, borderBottom: "1px solid var(--line)", flex: 1 }} className="kd-card">
            <div style={{ minWidth: 0 }}>
              <CatTag color={ci.color} size={11}>{ci.name}</CatTag>
              <h3 className="clamp-2 kd-title" style={{ margin: "8px 0 8px", fontSize: 16.5, fontWeight: 800, lineHeight: 1.34 }}>{a.title}</h3>
              <div style={{ marginBottom: 8 }}><Hashtags tags={a.tags} size={11.5} max={3} nowrap /></div>
              <MetaRow a={a} />
            </div>
            <Thumb src={a.image} hue={a.hue} ratio="1/1" radius={10} icon={false} />
          </article>
        );
      })}
      {sideList.length === 0 && (
        <div style={{ flex: 1, display: "grid", placeItems: "center", color: "var(--muted)", fontSize: 14, border: "1px dashed var(--line)", borderRadius: "var(--r)" }}>
          다른 기사가 없어요
        </div>
      )}
    </div>
  );

  // 헤드라인 히어로 카드 (헤드라인 뉴스 + NEWS 배너 공통)
  //  - isAd=true면 좌상단 "AD · 광고" 뱃지를 얹는다 (NEWS 배너)
  const headlineHero = (article, isAd) => {
    const ci = catInfo(article);
    return (
      <HeroCard
        article={article}
        onOpen={onOpen}
        radius="var(--r-lg)"
        overlay="linear-gradient(to top, rgba(8,16,40,.88) 0%, rgba(8,16,40,.4) 42%, rgba(8,16,40,.12) 70%, transparent 88%)"
        captionPad="28px 30px 20px"
        badge={isAd ? (
          <span style={{ position: "absolute", left: 18, top: 18, zIndex: 2, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(15, 23, 42, 0.78)", color: "#fff", fontSize: 12, fontWeight: 800, padding: "6px 12px", borderRadius: 99, backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)" }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: "rgb(255, 90, 77)" }} />
            {" AD · 광고"}
          </span>
        ) : null}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 13 }}>
          <CatTag color={ci.color} solid size={12.5}>{ci.name}</CatTag>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 800, background: "rgba(255,255,255,.2)", padding: "5px 11px", borderRadius: 7, backdropFilter: "blur(3px)" }}>
            <Ico.spark width={14} height={14} /> {isToday ? "오늘의 헤드라인" : "이 날의 헤드라인"}
          </span>
          {article.date && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 800, background: "rgba(255,255,255,.2)", padding: "5px 11px", borderRadius: 7, backdropFilter: "blur(3px)" }}>
              <Ico.clock width={14} height={14} /> {fmtKDate(article.date)}
            </span>
          )}
        </div>
        <h2 style={{ margin: 0, fontSize: 32, fontWeight: 900, lineHeight: 1.25, letterSpacing: "-.025em", maxWidth: 640 }} className="clamp-2">{article.title}</h2>
        <div style={{ marginTop: 12 }}><Hashtags tags={article.tags} color="rgba(255,255,255,.9)" size={12.5} max={3} /></div>
      </HeroCard>
    );
  };

  // 배너 1건 → 카드 (NEWS는 헤드라인형 + AD 뱃지, 날짜는 선택 날짜로 동기화 / URL은 이미지형)
  const bannerCard = (b) =>
    b.contentsType === "NEWS"
      ? headlineHero({ ...b, date }, true)
      : <NewsBannerCard banner={b} onOpen={onOpen} />;

  // 좌측 히어로: 배너 있으면 배너(2개↑ 슬라이드 / 1개 단일), 없으면 헤드라인 뉴스
  let leftHero = null;
  if (hasBanner) {
    leftHero = isMultiBanner ? (
      <Swiper
        className="kd-banner-swiper"
        modules={[Autoplay, Navigation, Pagination]}
        spaceBetween={0}
        slidesPerView={1}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop
        speed={500}
        navigation
        pagination={{ clickable: true }}
        style={{ width: "100%", height: "100%", minHeight: 420, borderRadius: "var(--r-lg)", overflow: "hidden" }}
      >
        {bannerList.map((b) => (
          <SwiperSlide key={b.bannerSeq || b.id}>{bannerCard(b)}</SwiperSlide>
        ))}
      </Swiper>
    ) : (
      bannerCard(bannerList[0])
    );
  } else if (headline) {
    leftHero = headlineHero(headline, false);
  }

  return (
    <section style={{ padding: "30px 0 8px" }}>
      {/* 날짜 바 (날짜 검색 — 두 모드 공통) */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 6, height: 22, borderRadius: 3, background: "var(--blue-600)" }} />
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900, letterSpacing: "-.02em" }}>{isToday ? "키즈다 뉴스" : `${fmtKDate(date)} 뉴스`}</h2>
          <button onClick={() => onMore && onMore(apiMode && headline ? headline.date : date)} className="kd-todaymore">더보기 <Ico.chevR width={15} height={15} /></button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setDate((d) => shiftDate(d, -1))} aria-label="이전 날짜" className="kd-datebtn"><Ico.chevL width={17} height={17} /></button>
          <div className="kd-datepick" style={{ position: "relative" }}>
            <Ico.clock width={16} height={16} style={{ color: "var(--blue-600)" }} />
            <span>{fmtKDate(date)}</span>
            <Ico.chevD width={14} height={14} style={{ color: "var(--muted)" }} />
            <input ref={dateInputRef} type="date" value={date} max={TODAY} onChange={(e) => e.target.value && setDate(e.target.value)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} aria-label="날짜 선택" />
          </div>
          <button onClick={() => setDate((d) => shiftDate(d, 1))} aria-label="다음 날짜" className="kd-datebtn" disabled={isToday} style={{ opacity: isToday ? .4 : 1, cursor: isToday ? "default" : "pointer" }}><Ico.chevR width={17} height={17} /></button>
          {!isToday && <button onClick={() => setDate(TODAY)} className="kd-todaybtn">오늘로</button>}
        </div>
      </div>

      {/* 메인 + 사이드 */}
      {initialLoading ? (
        <NewsHeroSkeleton />
      ) : leftHero ? (
        /* 좌측: 배너(1개 단일 / 2개↑ 슬라이드) 또는 헤드라인 + 우측 최신 뉴스 3개 */
        <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 24 }} className="kd-hero">
          {leftHero}
          {sideListEl}
        </div>
      ) : (
        <div style={{ padding: "60px", textAlign: "center", color: "var(--muted)", background: "#fff", border: "1px dashed var(--line)", borderRadius: "var(--r-lg)" }}>
          <div style={{ fontWeight: 700, color: "var(--ink-2)", fontSize: 16 }}>{fmtKDate(date)}에는 등록된 뉴스가 없어요</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>다른 날짜를 선택하거나 오늘로 돌아가 보세요.</div>
        </div>
      )}

      {/* 키즈다 PICK */}
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 38, marginBottom: 18 }}>
        <span style={{ width: 6, height: 20, borderRadius: 3, background: "var(--blue-600)" }} />
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: "-.02em" }}>키즈다 PICK</h2>
      </div>

      {/* 인기 뉴스 + 추천 뉴스 */}
      {loading ? <PickSkeleton /> : (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.45fr", gap: 24 }} className="kd-picks">
        {/* 인기 뉴스 */}
        <div style={{ background: "#fff", borderRadius: "var(--r)", padding: "20px 22px", boxShadow: "var(--sh-1)", border: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6, fontWeight: 800, fontSize: 15, color: "var(--c-onebite)" }}>
            <Ico.fire /> 인기 뉴스
          </div>
          <div style={{ display: "grid" }}>
            {popular.map((a, i) => (
              <article key={a.id} onClick={() => onOpen(a)} style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "26px 1fr", gap: 11, alignItems: "center", padding: "12px 0", borderBottom: i < popular.length - 1 ? "1px solid var(--line-2)" : "none" }} className="kd-rank">
                <span style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: i < 3 ? "var(--blue-600)" : "var(--muted-2)", textAlign: "center" }}>{i + 1}</span>
                <div style={{ minWidth: 0 }}>
                  <h4 className="clamp-1 kd-title" style={{ margin: 0, fontSize: 14.5, fontWeight: 700, lineHeight: 1.4 }}>{a.title}</h4>
                  <div style={{ marginTop: 4 }}><Hashtags tags={a.tags} size={10.5} max={3} nowrap /></div>
                  <div style={{ fontSize: 12, color: "var(--muted-2)", marginTop: 3, fontWeight: 600 }}>
                    {catInfo(a).name} · <Ico.eye style={{ verticalAlign: "-2px" }} /> {fmtViews(a.views)}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
        {/* 추천 뉴스 */}
        <div style={{ background: "#fff", borderRadius: "var(--r)", padding: "20px 22px", boxShadow: "var(--sh-1)", border: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 16, fontWeight: 800, fontSize: 15, color: "var(--blue-700)" }}>
            <Ico.spark width={17} height={17} /> 추천 뉴스
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16 }} className="kd-rec4">
            {recommend.map((a) => (
              <article key={a.id} onClick={() => onOpen(a)} style={{ cursor: "pointer" }} className="kd-card">
                <Thumb src={a.image} hue={a.hue} ratio="16/11" radius={9} icon={false} label={catInfo(a).name} />
                <h4 className="clamp-2 kd-title" style={{ margin: "9px 0 6px", fontSize: 13.5, fontWeight: 800, lineHeight: 1.34 }}>{a.title}</h4>
                <Hashtags tags={a.tags} size={10.5} max={3} nowrap />
              </article>
            ))}
          </div>
        </div>
      </div>
      )}
    </section>
  );
}

/* ---------- 카테고리 블록 (홈 미리보기) ---------- */
export function CornerBlock({ corner, articles, onOpen, onMore }) {
  if (articles.length === 0) return null;
  const [lead, ...rest] = articles;
  return (
    <section style={{ padding: "8px 0" }}>
      <SectionHead color={corner.color} eyebrow={corner.name} title={corner.desc || corner.name} more={onMore ? () => onMore(corner.key) : null} />
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr minmax(0, 1fr) minmax(0, 1fr)", gap: 24 }} className="kd-corner">
        <article onClick={() => onOpen(lead)} style={{ cursor: "pointer" }} className="kd-card">
          <Thumb src={lead.image} hue={lead.hue} ratio="16/10" radius={12} label={corner.name} />
          <div style={{ marginTop: 12 }}>
            <Hashtags tags={lead.tags} color={corner.color} size={12.5} max={3} />
            <h3 className="clamp-2 kd-title" style={{ margin: "7px 0 9px", fontSize: 19, fontWeight: 800, lineHeight: 1.32 }}>{lead.title}</h3>
            <p className="clamp-2" style={{ margin: "0 0 10px", fontSize: 14, color: "var(--muted)", lineHeight: 1.55 }}>{lead.summary}</p>
            <MetaRow a={lead} />
          </div>
        </article>
        <div style={{ gridColumn: "span 2", display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "4px 24px", alignContent: "start" }}>
          {rest.slice(0, 4).map((a) => (
            <article key={a.id} onClick={() => onOpen(a)} style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 92px", gap: 13, padding: "13px 0", borderBottom: "1px solid var(--line)", alignItems: "center" }} className="kd-card">
              <div style={{ minWidth: 0 }}>
                <h4 className="clamp-2 kd-title" style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 800, lineHeight: 1.34 }}>{a.title}</h4>
                <div style={{ marginBottom: 8 }}><Hashtags tags={a.tags} size={11} max={3} nowrap /></div>
                <MetaRow a={a} />
              </div>
              <Thumb src={a.image} hue={a.hue} ratio="1/1" radius={9} icon={false} />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 카테고리 블록 (API 연동) ----------
   1차 카테고리(topicCategorySeq)별로 NEWS_ALL 뉴스를 조회해 CornerBlock 렌더.
   데이터 없으면(또는 로딩 중) 아무것도 표시하지 않음. */
export function ApiCornerBlock({ corner, onOpen, onMore }) {
  const cacheKey = `cat:${corner.key}`;
  const [items, setItems] = useState(() => (_kdCache.has(cacheKey) ? _kdCache.get(cacheKey) : null));
  useEffect(() => {
    // 캐시에 있으면 복원만 (상세→뒤로가기 시 재호출 안 함)
    if (_kdCache.has(cacheKey)) {
      setItems(_kdCache.get(cacheKey));
      return;
    }
    let alive = true;
    // 동일 카테고리 요청이 진행 중이면 그 Promise를 공유 (중복 호출 방지)
    let p = _kdInflight.get(cacheKey);
    if (!p) {
      p = getNewsByCategory({ topicCategorySeq: corner.key, size: 6 }).then((list) =>
        Array.isArray(list) ? list : []
      );
      _kdInflight.set(cacheKey, p);
      p.finally(() => _kdInflight.delete(cacheKey));
    }
    p.then((v) => {
      _kdCache.set(cacheKey, v);
      if (alive) setItems(v);
    });
    return () => {
      alive = false;
    };
  }, [corner.key, cacheKey]);

  if (!items || items.length === 0) return null;

  // categoryList 항목엔 desc가 없으므로 정적 매핑에서 보강(있으면)
  const enriched = corner.desc
    ? corner
    : { ...corner, desc: descByName[normalizeCat(corner.name)] || "" };

  return <CornerBlock corner={enriched} articles={items} onOpen={onOpen} onMore={onMore} />;
}

/* ---------- 구독 CTA ---------- */
export function SubscribeCTA() {
  const navigate = useNavigate();
  return (
    <section style={{ background: "linear-gradient(120deg, var(--blue-700), var(--blue-600))", borderRadius: "var(--r-xl)", padding: "44px 44px", color: "#fff", margin: "44px 0", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", right: -40, top: -40, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,.08)" }} />
      <div style={{ position: "absolute", right: 120, bottom: -60, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
        <div>
          <div className="kicker" style={{ color: "var(--blue-100)" }}>KIDSDA SUBSCRIPTION</div>
          <h2 style={{ margin: "10px 0 8px", fontSize: 30, fontWeight: 900, letterSpacing: "-.02em" }}>매일 아침, 교과서가 살아있는 뉴스를</h2>
          <p style={{ margin: 0, fontSize: 16, color: "rgba(255,255,255,.85)" }}>초등 교과 연계 기사 · 영어뉴스 · 톡톡 토론까지. 매일 아침 키즈다와 함께 세상을 읽어요.</p>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={() => navigate(APP_DOWNLOAD_PATH)} style={{ background: "#fff", color: "var(--blue-700)", fontWeight: 800, fontSize: 15.5, padding: "14px 28px", borderRadius: 99, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Ico.download width={18} height={18} />
            앱 다운로드
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- Search results ---------- */
const SEARCH_PAGE_SIZE = 20;
export function SearchResults({ query, onOpen, onClear }) {
  const cacheKey = `search:${query}`;
  const _c = _kdCache.get(cacheKey) || {};
  const [page, setPage] = useState(_c.page ?? 0);
  const [data, setData] = useState(_c.data ?? null);
  const [loading, setLoading] = useState(_c.data == null);

  // 검색어 변경 시 페이지 초기화(캐시 있으면 복원)
  useEffect(() => {
    setPage(_kdCache.get(`search:${query}`)?.page ?? 0);
  }, [query]);

  // 제목 검색 (searchPostsWithFiles, title=query) — 캐시 동일 페이지면 재호출 안 함
  useEffect(() => {
    if (!query) return;
    const ck = `search:${query}`;
    const cc = _kdCache.get(ck);
    if (cc && cc.data && cc.page === page) {
      setData(cc.data);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    getNewsSearch({ query, size: SEARCH_PAGE_SIZE, page }).then((res) => {
      if (!alive) return;
      const v = res || { items: [], totalPages: 1, totalElements: 0 };
      setData(v);
      _kdCache.set(ck, { page, data: v });
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    return () => {
      alive = false;
    };
  }, [query, page]);

  const items = data?.items || [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages || 1;
  const showSkeleton = loading && !data;
  const empty = !loading && items.length === 0;

  return (
    <section style={{ padding: "30px 0" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <h2 style={{ margin: 0, fontSize: 23, fontWeight: 900 }}>
          <span style={{ color: "var(--blue-600)" }}>'{query}'</span> 검색 결과
          {!showSkeleton && <span style={{ color: "var(--muted)", fontWeight: 700 }}> {totalElements}건</span>}
        </h2>
        <button onClick={onClear} style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-2)", display: "inline-flex", alignItems: "center", gap: 5 }}>
          <Ico.back width={16} height={16} /> 홈으로
        </button>
      </div>
      {showSkeleton ? (
        <GridSkeleton count={SEARCH_PAGE_SIZE} />
      ) : empty ? (
        <div style={{ padding: "70px 0", textAlign: "center", color: "var(--muted)" }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink-2)" }}>검색 결과가 없어요</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>다른 단어로 검색하거나 카테고리에서 둘러보세요.</div>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 24, opacity: loading ? 0.5 : 1, transition: "opacity .15s" }} className="kd-grid4">
            {items.map((a) => <CardStd key={a.id} a={a} onOpen={onOpen} />)}
          </div>
          <Pager page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </section>
  );
}

/* ---------- 코너 전용 페이지 리스트 행 ---------- */
function CornerListRow({ a, onOpen, accent }) {
  return (
    <article onClick={() => onOpen(a)} style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "196px 1fr", gap: 22, padding: "22px 0", borderBottom: "1px solid var(--line)", alignItems: "center" }} className="kd-card">
      <Thumb src={a.image} hue={a.hue} ratio="16/10" radius={11} icon={false} />
      <div style={{ minWidth: 0 }}>
        <Hashtags tags={a.tags} color={accent || "var(--muted-2)"} size={12.5} max={3} />
        <h3 className="clamp-2 kd-title" style={{ margin: "9px 0 8px", fontSize: 20, fontWeight: 800, lineHeight: 1.33, letterSpacing: "-.01em" }}>{a.title}</h3>
        <p className="clamp-2" style={{ margin: "0 0 11px", fontSize: 14.5, color: "var(--muted)", lineHeight: 1.6 }}>{a.summary}</p>
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12.5, color: "var(--muted)", fontWeight: 600 }}>
          <span style={{ fontWeight: 800, color: "var(--ink-2)" }}>{a.author}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Ico.clock /> {relDate(a.date)}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Ico.eye /> {fmtViews(a.views)}</span>
        </div>
      </div>
    </article>
  );
}

/* ---------- 페이지네이션 ---------- */
function Pager({ page, totalPages, onChange }) {
  if (!totalPages || totalPages <= 1) return null;
  const cur = page + 1; // 1-based 표시
  const win = 2;
  const start = Math.max(1, cur - win);
  const end = Math.min(totalPages, cur + win);
  const nums = [];
  for (let i = start; i <= end; i++) nums.push(i);
  const btn = (label, to, disabled, active) => (
    <button
      key={`${label}-${to}`}
      disabled={disabled}
      onClick={() => !disabled && onChange(to)}
      style={{
        minWidth: 38, height: 38, borderRadius: 9, padding: "0 11px",
        fontWeight: 800, fontSize: 14,
        border: "1px solid " + (active ? "var(--blue-600)" : "var(--line)"),
        background: active ? "var(--blue-600)" : "#fff",
        color: active ? "#fff" : disabled ? "var(--muted-2)" : "var(--ink-2)",
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 36, flexWrap: "wrap" }}>
      {btn("‹", page - 1, cur === 1)}
      {start > 1 && <span style={{ color: "var(--muted-2)", padding: "0 2px" }}>…</span>}
      {nums.map((n) => btn(String(n), n - 1, false, n === cur))}
      {end < totalPages && <span style={{ color: "var(--muted-2)", padding: "0 2px" }}>…</span>}
      {btn("›", page + 1, cur === totalPages)}
    </div>
  );
}

/* ---------- 오늘 뉴스 전체 페이지 (날짜별 전체 기사 · 20개씩 페이징) ----------
   - 라우트 :date 의 기사를 API로 조회 (데이터 없으면 가장 가까운 이전 날짜로 확정)
   - 20개씩 페이지네이션, API 실패 시 정적 데이터로 폴백(클라이언트 페이징) */
const TODAY_ALL_PAGE_SIZE = 20;
export function TodayAllPage({ date, articles, onOpen, onBack }) {
  const cacheKey = `today:${date}`;
  const _c = _kdCache.get(cacheKey) || {};
  const [resolvedDate, setResolvedDate] = useState(_c.resolvedDate ?? date);
  const [page, setPage] = useState(_c.page ?? 0); // 0-based
  const [data, setData] = useState(_c.data ?? null); // {items,totalPages,totalElements} | null
  const [apiFailed, setApiFailed] = useState(false);
  const [loading, setLoading] = useState(_c.data == null);

  // 라우트 날짜 변경 → 데이터 있는 가장 가까운 날짜로 확정 (캐시 있으면 복원, 재확정 안 함)
  useEffect(() => {
    if (_kdCache.get(cacheKey)?.resolvedDate) return;
    let alive = true;
    setPage(0);
    setLoading(true);
    resolveNewsDate(date).then((rd) => {
      if (alive) setResolvedDate(rd || date);
    });
    return () => {
      alive = false;
    };
  }, [date, cacheKey]);

  // 확정 날짜/페이지 변경 시 해당 페이지 조회 (캐시에 동일 페이지가 있으면 재호출 안 함)
  useEffect(() => {
    const cc = _kdCache.get(cacheKey);
    if (cc && cc.data && cc.resolvedDate === resolvedDate && cc.page === page) {
      setData(cc.data);
      setApiFailed(false);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    getNewsAllPaged({ date: resolvedDate, size: TODAY_ALL_PAGE_SIZE, page }).then((res) => {
      if (!alive) return;
      if (res && Array.isArray(res.items)) {
        setData(res);
        setApiFailed(false);
        _kdCache.set(cacheKey, { resolvedDate, page, data: res });
      } else {
        setData(null);
        setApiFailed(true); // API 실패 → 정적 폴백
      }
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    return () => {
      alive = false;
    };
  }, [resolvedDate, page, cacheKey]);

  // 정적 폴백 (API 실패 시)
  const staticDay = articles.filter((a) => a.date === resolvedDate);
  const staticItems = staticDay.length
    ? staticDay
    : [...articles].sort((a, b) => (b.date < a.date ? -1 : b.date > a.date ? 1 : 0));

  let items, totalPages, totalElements;
  if (data && !apiFailed) {
    items = data.items;
    totalPages = data.totalPages || 1;
    totalElements = data.totalElements ?? data.items.length;
  } else if (apiFailed) {
    // 정적 폴백은 API 실패 시에만 (로딩 중에는 데모를 보이지 않음)
    totalElements = staticItems.length;
    totalPages = Math.max(1, Math.ceil(totalElements / TODAY_ALL_PAGE_SIZE));
    items = staticItems.slice(page * TODAY_ALL_PAGE_SIZE, (page + 1) * TODAY_ALL_PAGE_SIZE);
  } else {
    items = [];
    totalPages = 1;
    totalElements = 0;
  }
  const hasData = (data && !apiFailed) || apiFailed; // 데이터 확정(성공/실패폴백)
  const showSkeleton = loading && !hasData; // 로딩 중 + 아직 데이터 없음 → 기본화면
  const empty = !loading && (!items || items.length === 0);

  return (
    <section style={{ padding: "26px 0 8px" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 22, gap: 16, flexWrap: "wrap" }}>
        <div>
          <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13.5, fontWeight: 700, color: "var(--muted)", marginBottom: 12 }}><Ico.back width={16} height={16} /> 홈으로</button>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <span style={{ width: 8, height: 30, borderRadius: 4, background: "var(--blue-600)" }} />
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 900, letterSpacing: "-.025em" }}>{resolvedDate === TODAY ? "키즈다 뉴스" : `${fmtKDate(resolvedDate)} 뉴스`}</h1>
          </div>
          <p style={{ margin: "10px 0 0 19px", fontSize: 15, color: "var(--muted)" }}>
            {fmtKDate(resolvedDate)}{showSkeleton ? "" : empty ? " · 등록된 기사가 없어요" : ` · 이 날의 모든 기사 ${totalElements}건`}
          </p>
        </div>
        {!showSkeleton && !empty && <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--muted)" }}>총 {totalElements}건</span>}
      </div>

      {showSkeleton ? (
        <GridSkeleton count={20} />
      ) : empty ? (
        <div style={{ padding: "60px", textAlign: "center", color: "var(--muted)", background: "#fff", border: "1px dashed var(--line)", borderRadius: "var(--r-lg)" }}>
          <div style={{ fontWeight: 700, color: "var(--ink-2)", fontSize: 16 }}>등록된 기사가 없어요</div>
          <div style={{ fontSize: 14, marginTop: 6 }}>다른 날짜를 선택하거나 홈으로 돌아가 보세요.</div>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 24, opacity: loading ? 0.5 : 1, transition: "opacity .15s" }} className="kd-grid4">
            {items.map((a) => <CardStd key={a.id} a={a} onOpen={onOpen} />)}
          </div>
          <Pager page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </section>
  );
}

/* ---------- 카테고리 전용 페이지 (API 연동 · 20개씩 페이징, 날짜 무관 최신순) ---------- */
const CORNER_PAGE_SIZE = 20;
export function CornerPage({ corner, items: staticItems = [], onOpen, onBack }) {
  const cacheKey = `corner:${corner.key}`;
  const _c = _kdCache.get(cacheKey) || {};
  const [page, setPage] = useState(_c.page ?? 0); // 0-based
  const [data, setData] = useState(_c.data ?? null); // {items,totalPages,totalElements} | null
  const [apiFailed, setApiFailed] = useState(false);
  const [loading, setLoading] = useState(_c.data == null);
  const c = corner.color;

  // 카테고리가 바뀌면(컴포넌트 유지된 채 corner.key 변경) 해당 카테고리의 캐시 페이지로 복원/초기화
  useEffect(() => {
    setPage(_kdCache.get(`corner:${corner.key}`)?.page ?? 0);
  }, [corner.key]);

  // topicCategorySeq(corner.key) + page 로 조회 (캐시에 동일 페이지가 있으면 재호출 안 함)
  useEffect(() => {
    const cc = _kdCache.get(cacheKey);
    if (cc && cc.data && cc.page === page) {
      setData(cc.data);
      setApiFailed(false);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    getNewsByCategoryPaged({ topicCategorySeq: corner.key, size: CORNER_PAGE_SIZE, page }).then((res) => {
      if (!alive) return;
      if (res && Array.isArray(res.items)) {
        setData(res);
        setApiFailed(false);
        _kdCache.set(cacheKey, { page, data: res });
      } else {
        setData(null);
        setApiFailed(true);
      }
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    return () => {
      alive = false;
    };
  }, [corner.key, page, cacheKey]);

  // 정적 폴백은 API 실패 시에만 (로딩 중에는 데모를 보이지 않음)
  let items, totalPages, totalElements;
  if (data && !apiFailed) {
    items = data.items;
    totalPages = data.totalPages || 1;
    totalElements = data.totalElements ?? data.items.length;
  } else if (apiFailed) {
    totalElements = staticItems.length;
    totalPages = Math.max(1, Math.ceil(totalElements / CORNER_PAGE_SIZE));
    items = staticItems.slice(page * CORNER_PAGE_SIZE, (page + 1) * CORNER_PAGE_SIZE);
  } else {
    items = [];
    totalPages = 1;
    totalElements = 0;
  }
  const hasData = (data && !apiFailed) || apiFailed;
  const showSkeleton = loading && !hasData; // 로딩 중 + 아직 데이터 없음 → 기본화면
  const empty = !loading && (!items || items.length === 0);

  return (
    <section style={{ padding: "26px 0 8px" }}>
      {/* corner header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 22, gap: 16, flexWrap: "wrap" }}>
        <div>
          <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13.5, fontWeight: 700, color: "var(--muted)", marginBottom: 12 }}><Ico.back width={16} height={16} /> 전체 뉴스</button>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <span style={{ width: 8, height: 30, borderRadius: 4, background: c }} />
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 900, letterSpacing: "-.025em" }}>{corner.name}</h1>
          </div>
          {corner.desc && <p style={{ margin: "10px 0 0 19px", fontSize: 15, color: "var(--muted)" }}>{corner.desc}</p>}
        </div>
        {!showSkeleton && !empty && <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--muted)" }}>총 {totalElements}건</span>}
      </div>

      {showSkeleton ? (
        <GridSkeleton count={20} />
      ) : empty ? (
        <div style={{ padding: "38px", textAlign: "center", color: "var(--muted)", background: "#fff", border: "1px dashed var(--line)", borderRadius: "var(--r-lg)" }}>
          <div style={{ fontWeight: 700, color: "var(--ink-2)" }}>이 카테고리의 기사를 준비 중이에요.</div>
          <div style={{ fontSize: 13.5, marginTop: 5 }}>새 기사가 등록되면 이곳에 차례로 올라옵니다.</div>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 24, opacity: loading ? 0.5 : 1, transition: "opacity .15s" }} className="kd-grid4">
            {items.map((a) => <CardStd key={a.id} a={a} onOpen={onOpen} />)}
          </div>
          <Pager page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </section>
  );
}

/* ---------- 랜딩 배너 공통 (searchWebBannersWithFiles) ----------
   배너가 여러 개면 슬라이드(자동 넘김)로 한 장씩, 한 개면 그대로 노출.
   클릭 시 contentsType: URL → 외부 링크 새 창, NEWS → 기사 상세로 이동. */
function useLandingBannerOpen() {
  const navigate = useNavigate();
  return (b) => {
    if (!b?.contentsRef) return;
    if (b.contentsType === "NEWS") {
      navigate(`/kizda_news/article/${b.contentsRef}`);
    } else {
      window.open(b.contentsRef, "_blank", "noopener,noreferrer");
    }
  };
}

function LandingBannerSlides({ items, height, autoSlideInterval = 5000 }) {
  const openBanner = useLandingBannerOpen();
  const slide = (b) => (
    <button
      type="button"
      onClick={() => openBanner(b)}
      aria-label={b.title || "배너"}
      style={{ display: "block", width: "100%", height: height || "auto", padding: 0, border: "none", background: "none", cursor: b.contentsRef ? "pointer" : "default" }}
    >
      <img
        src={b.image}
        alt={b.title || "배너"}
        style={{ display: "block", width: "100%", height: height ? "100%" : "auto", objectFit: height ? "cover" : undefined }}
      />
    </button>
  );

  // 한 장이면 슬라이더 없이 그대로
  if (items.length === 1) return slide(items[0]);

  return (
    <Swiper
      modules={[Autoplay]}
      spaceBetween={0}
      slidesPerView={1}
      autoplay={{ delay: autoSlideInterval, disableOnInteraction: false }}
      loop={true}
      speed={500}
      style={{ width: "100%", height: height || "auto" }}
    >
      {items.map((b) => (
        <SwiperSlide key={b.id}>{slide(b)}</SwiperSlide>
      ))}
    </Swiper>
  );
}

/* ---------- 가로형 광고 배너 (태블릿/모바일 — 사이드 배너 대체) ----------
   데스크톱(>1560px)에서는 숨김, 사이드 배너가 사라지는 1560px 이하에서만 노출.
   CornerNav 와 "키즈다 뉴스" 섹션 사이 상단에 표시.
   API 랜딩 배너(가로 이미지, files.type=1022)가 있으면 그걸 노출하고,
   없으면 기존 정적 광고로 폴백. */
export function TopAdBanner({ banners, loading }) {
  const navigate = useNavigate();

  const apiBanners = (banners || [])
    .filter((b) => b.horizontalImage)
    .map((b) => ({ ...b, image: b.horizontalImage }));

  // 로딩 중 → 배너 영역 스켈레톤
  if (loading) {
    return (
      <div className="kd-topbanner" style={{ background: "none", boxShadow: "none" }}>
        <Skel h={96} style={{ borderRadius: "var(--r-lg)" }} />
      </div>
    );
  }

  if (apiBanners.length > 0) {
    return (
      <div className="kd-topbanner" style={{ background: "none", boxShadow: "none" }}>
        <LandingBannerSlides items={apiBanners} />
      </div>
    );
  }

  return (
    <button type="button" className="kd-topbanner" onClick={() => navigate(APP_DOWNLOAD_PATH)} aria-label="키즈다 신문 구독 광고">
      <div className="kd-topbanner-deco kd-topbanner-deco-1" />
      <div className="kd-topbanner-deco kd-topbanner-deco-2" />
      <div className="kd-topbanner-inner">
        <div className="kd-topbanner-text">
          <span className="kd-topbanner-tag">AD · 광고 · 키즈다 신문 구독</span>
          <strong className="kd-topbanner-title">매일 아침 배달되는 어린이 뉴스</strong>
          <span className="kd-topbanner-desc">교과 연계 기사부터 영어뉴스·토론까지, 키즈다와 함께 세상을 읽어요.</span>
        </div>
        <span className="kd-topbanner-btn">
          <Ico.download width={16} height={16} />
          앱 다운로드
        </span>
      </div>
    </button>
  );
}

/* ---------- 세로형 광고 배너 (사이드 스카이스크래퍼) ----------
   API 랜딩 배너(세로 이미지, files.type=1023)가 있으면 그걸 노출하고,
   없으면 기존 정적 광고로 폴백. */
export function SideAdRail({ banners, loading }) {
  const navigate = useNavigate();
  const [closed, setClosed] = useState(false);
  const [top, setTop] = useState(146);
  useEffect(() => {
    // 파란 카테고리바(sticky)의 실제 하단 위치를 측정해 그 아래(GAP)로 정렬.
    // 스크롤로 카테고리바가 최상단에 붙으면 배너도 함께 올라가 따라붙는다.
    const GAP = 14;
    const update = () => {
      const nav = document.querySelector(".kd-cornernav");
      const navBottom = nav ? nav.getBoundingClientRect().bottom : 52;
      setTop(Math.max(0, navBottom) + GAP);
    };
    update();
    // capture: true → window 외 다른 스크롤 컨테이너(html/body 등)에서도 동작
    window.addEventListener("scroll", update, { passive: true, capture: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, []);
  if (closed) return null;

  const apiBanners = (banners || [])
    .filter((b) => b.verticalImage)
    .map((b) => ({ ...b, image: b.verticalImage }));

  return (
    <aside className="kd-adrail" aria-label="광고" style={{ top }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px" }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".08em", color: "var(--muted-2)" }}>AD · 광고</span>
          <button onClick={() => setClosed(true)} aria-label="광고 닫기" style={{ width: 18, height: 18, borderRadius: 99, display: "grid", placeItems: "center", color: "var(--muted-2)", background: "var(--line-2)" }}><Ico.close width={11} height={11} /></button>
        </div>
        {/* 세로형 배너 슬롯 (160x600) — 로딩 중 스켈레톤 → API 배너 → 정적 광고 폴백 */}
        {loading ? (
          <Skel h={600} style={{ width: 160, borderRadius: 14 }} />
        ) : apiBanners.length > 0 ? (
          <div style={{ width: 160, height: 600, borderRadius: 14, overflow: "hidden", border: "1px solid var(--line)", boxShadow: "var(--sh-1)" }}>
            <LandingBannerSlides items={apiBanners} height={600} />
          </div>
        ) : (
        <button onClick={() => navigate(APP_DOWNLOAD_PATH)} style={{ width: 160, height: 600, borderRadius: 14, overflow: "hidden", border: "1px solid var(--line)", background: "linear-gradient(160deg, var(--blue-600), var(--blue-700))", color: "#fff", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "24px 18px", boxShadow: "var(--sh-1)" }}>
          <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,.1)" }} />
          <div style={{ position: "absolute", bottom: -40, left: -30, width: 130, height: 130, borderRadius: "50%", background: "rgba(255,255,255,.07)" }} />
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "var(--blue-100)", marginBottom: 10 }}>키즈다 신문 구독</div>
            <div style={{ fontSize: 21, fontWeight: 900, lineHeight: 1.3, letterSpacing: "-.02em" }}>매일 아침<br />배달되는<br />어린이 뉴스</div>
            <div style={{ fontSize: 12.5, color: "rgba(255,255,255,.85)", margin: "14px 0 18px", lineHeight: 1.55 }}>교과 연계 기사부터<br />영어뉴스·토론까지</div>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", color: "var(--blue-700)", fontWeight: 800, fontSize: 12.5, padding: "9px 16px", borderRadius: 99 }}>
              <Ico.download width={14} height={14} />
              앱 다운로드</span>
          </div>
        </button>
        )}
        <div style={{ fontSize: 10, color: "var(--muted-2)", textAlign: "center" }}>광고 문의 ad@kidsda.com</div>
      </div>
    </aside>
  );
}

/* ---------- 기사 상세 페이지 (모달 대신 페이지 이동) ---------- */
export function ArticlePage({ a, related, onOpen, onBack }) {
  const c = catInfo(a);
  const body = a.body || `${a.summary}\n\n키즈다는 어린이의 눈높이에 맞춰 세상의 소식을 쉽고 정확하게 전합니다. 이 기사는 교과 과정과 연계해 ${c.name} 내용을 쉽게 풀어내도록 구성되었어요.\n\n오늘의 이야기를 읽고, 친구들과 함께 생각을 나누어 보세요. 아래의 '톡톡 토론'에서 여러분의 의견도 들려줄 수 있답니다.`;

  // 공유: 현재 기사 URL을 클립보드에 복사
  const [copied, setCopied] = useState(false);
  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        // http/구형 브라우저 폴백
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.error("링크 복사 실패:", e);
      window.prompt("링크를 복사해 주세요", url);
    }
  };
  return (
    <article style={{ padding: "26px 0 8px" }}>
      <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13.5, fontWeight: 700, color: "var(--muted)", marginBottom: 18 }}><Ico.back width={16} height={16} /> 목록으로</button>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        {/* 헤더 */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
          <CatTag color={c.color} solid>{c.name}</CatTag>
          <Hashtags tags={a.tags} color="var(--muted)" size={13} />
        </div>
        <h1 style={{ margin: 0, fontSize: 34, fontWeight: 900, lineHeight: 1.28, letterSpacing: "-.025em" }}>{a.title}</h1>
        {/* <p style={{ margin: "14px 0 0", fontSize: 17, color: "var(--ink-2)", lineHeight: 1.6, fontWeight: 600 }}>{a.summary}</p> */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "20px 0", paddingBottom: 18, borderBottom: "1px solid var(--line)", fontSize: 13.5, color: "var(--muted)", fontWeight: 600 }}>
          <span style={{ fontWeight: 800, color: "var(--ink-2)" }}>{a.author}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Ico.clock /> {a.date}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Ico.eye /> {fmtViews(a.views)}</span>
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8, position: "relative" }}>
            {copied && (
              <span style={{ position: "absolute", right: 0, bottom: "calc(100% + 8px)", whiteSpace: "nowrap", background: "var(--ink)", color: "#fff", fontSize: 12, fontWeight: 700, padding: "6px 10px", borderRadius: 8, boxShadow: "var(--sh-2)" }}>
                링크가 복사되었어요
              </span>
            )}
            <button className="kd-iconbtn" onClick={handleCopyLink} aria-label="링크 복사" title="링크 복사"><Ico.share /></button>
          </span>
        </div>
        {/* 대표 이미지 (Quill 본문에는 이미지가 포함되므로 API 기사면 생략) */}
        {!a.content && <Thumb src={a.image} hue={a.hue} ratio="16/8" radius={16} icon={true} style={{ marginBottom: 28 }} />}
        {/* 본문: Quill Delta면 Quill 스타일로, 아니면 기존 RichBody */}
        {a.content ? (
          <QuillContent content={a.content} className="kd-richbody-lg" />
        ) : (
          <RichBody body={body} className="kd-richbody-lg" />
        )}
        {/* 함께 읽어요 */}
        {related && related.length > 0 && (
          <div style={{ marginTop: 40, paddingTop: 28, borderTop: "1px solid var(--line)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
              <span style={{ width: 6, height: 20, borderRadius: 3, background: "var(--blue-600)" }} />
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>이 기사와 함께 읽어요</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 24 }} className="kd-rel2">
              {related.map((r) => <CardStd key={r.id} a={r} onOpen={onOpen} />)}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export { CORNERS, cornerOf, topicOf };
